/**
 * Marker and Label — `docs/property-maps-v2.md` §5.3.
 *
 * One click, one point. They are the same tool with two kinds, because the
 * difference is entirely in how the thing is drawn afterwards: a marker wears
 * a glyph, a label is words on the photo. Both are a `Point`, and neither has
 * anything to drag.
 */
import type { Draft, DrawEvent, DrawTool, KeyEvent, ScreenPoint, ToolOptions } from './types.js'

export class PointTool implements DrawTool {
  readonly kind: 'marker' | 'label'

  readonly hint: string

  readonly autoFinish = true


  private readonly frame: ToolOptions['frame']

  private readonly snap: NonNullable<ToolOptions['snap']>

  private at: ScreenPoint | null = null

  constructor(kind: 'marker' | 'label', options: ToolOptions) {
    this.kind = kind
    this.frame = options.frame
    this.snap = options.snap ?? ((point) => point)
    this.hint = kind === 'marker' ? 'Click where the mark goes' : 'Click where the words go'
  }

  onPointerDown(event: DrawEvent): void {
    this.at = this.snap(event.point, event)
  }

  onPointerMove(): void { /* Nothing follows the pointer. */ }

  onPointerUp(): void { /* The click placed it. */ }

  onKey(event: KeyEvent): void {
    if (event.key === 'Escape') this.cancel()
  }

  cancel(): void { this.at = null }

  draft(): Draft | null {
    if (!this.at) return null
    return {
      kind: this.kind,
      geometry: { type: 'Point', coordinates: this.frame.toLngLat(this.at) },
      complete: true,
    }
  }

  finish(): Draft | null {
    const made = this.draft()
    if (made) this.cancel()
    return made
  }
}
